import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from "@angular/forms";
import {NgForOf, NgIf} from "@angular/common";
import {finalize, Subject, takeUntil} from 'rxjs';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../toast/toast.service';
import {UserService} from '../../services/user-service.service';
import {AuthService} from '../../services/auth.service';
import {Tag} from '../../models/tag';
import Place = google.maps.Place;
import {User} from '../../models/user';
import {Profile} from '../../models/profile';
import {LinkifyPipe} from '../pipes/linkify.pipe';
import {ProfileService} from '../../services/profile.service';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {ConversationService} from '../../services/conversation.service';
import {RouterLink} from '@angular/router';
import {TagService} from '../../services/tag.service';

@Component({
  selector: 'app-profile-modal',
  imports: [
    FormsModule,
    NgIf,
    LinkifyPipe,
    CdkTextareaAutosize,
    RouterLink,
    NgForOf
  ],
  templateUrl: './profile-modal.component.html',
  styleUrl: './profile-modal.component.scss'
})
export class ProfileModalComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() currentUser!: User
  @Input() profileUserId!: string

  // Profile
  isProfileLoading = false
  isProfileLoaded = false
  profile: Profile | null = null

  // Place (for Merchants)
  isPlaceLoading = false
  isPlaceLoaded = false
  isProfileEditable = false
  place: Place | null = null

  // Tags
  isTagsLoading = false
  isTagsLoaded = true;
  tags: Tag[] = []

  // Message
  isMessageRequired = false
  isMessageSent = false;
  message: string | null = null

  // Calculated profile
  description: string | null = null
  favoriteSpaces: string | null = null
  interests: Tag[] = []
  interestIds: string[] = []

  isToggleDescription = false
  isToggleFavoriteSpaces = false
  isToggleInterest = false

  submitted = false;

  constructor(private modal: NgbActiveModal,
              private toast: ToastService,
              private userService: UserService,
              private authService: AuthService,
              private profileService: ProfileService,
              private tagService: TagService,
              private conversationService: ConversationService) {
  }


  ngOnInit() {
    this.refreshProfile()
    this.refreshTags();
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  refreshProfile() {
    this.isProfileLoading = true
    this.profileService.getByUserId(this.profileUserId).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isProfileLoading = false
        this.isProfileLoaded = true
      }))
      .subscribe(profile => {
        this.profile = profile
        this.calculateProfile();
      })
  }

  refreshTags() {
    if (!this.currentUser) return

    const regionSlug = this.currentUser.region;

    this.isTagsLoading = true
    this.tagService.findByUserActiveRegion(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isTagsLoading = false
        this.isTagsLoaded = true
      }))
      .subscribe(tags => {
        this.tags = tags;
      });
  }

  calculateProfile() {
    if (!this.profile) return;

    // Current user
    if (this.currentUser._id === this.profile.user._id) {
      this.isProfileEditable = true
      this.description = this.profile.description || ''
      this.favoriteSpaces = this.profile.favoriteSpaces || ''
      this.interests = this.profile.tags || []
      this.interestIds = this.profile.tags.map(tag => tag._id)
    }
  }

  submit() {

    // Show the message form
    if (!this.isMessageRequired) {
      this.isMessageRequired = true;
      return;
    }

    if (!this.currentUser) return;
    if (!this.profile) return;
    if (!this.message) return;

    const userIds = [this.currentUser._id, this.profile.user._id]
    const message = this.message

    this.conversationService.createConversation(userIds, message).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(() => {

        // Reset form
        this.message = null
        this.isMessageRequired = false;

        this.isMessageSent = true;
      });
  }

  close() {
    this.modal.close()
  }

  toggleDescription() {
    this.isToggleDescription = !this.isToggleDescription
  }

  toggleFavoriteSpaces() {
    this.isToggleFavoriteSpaces = !this.isToggleFavoriteSpaces
  }

  toggleInterests() {
    this.isToggleInterest = !this.isToggleInterest
  }

  updateDescription() {
    if (!this.profile) return;
    if (!this.description) return;
    if (!this.favoriteSpaces) return;
    if (!this.interestIds) return;

    const profileId = this.profile._id
    const description = this.description
    const favoriteSpaces = this.favoriteSpaces
    const tagIds = this.interestIds

    this.profileService.updateProfile(profileId, description, favoriteSpaces, tagIds).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(() => {
        this.refreshProfile()
        this.isToggleDescription = false
      })
  }

  updateFavoriteSpaces() {
    if (!this.profile) return;
    if (!this.description) return;
    if (!this.favoriteSpaces) return;
    if (!this.interestIds) return;

    const profileId = this.profile._id
    const description = this.description
    const favoriteSpaces = this.favoriteSpaces
    const tagIds = this.interestIds

    this.profileService.updateProfile(profileId, description, favoriteSpaces, tagIds).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(() => {
        this.refreshProfile()
        this.isToggleFavoriteSpaces = false
      })
  }

  updateInterests(tag: Tag) {
    if (!this.profile) return;
    if (!this.description) return;
    if (!this.favoriteSpaces) return;
    if (!this.interestIds) return;

    this.interestIds.includes(tag._id)
      ? this.interestIds.splice(this.interestIds.indexOf(tag._id), 1)
      : this.interestIds.push(tag._id)

    const profileId = this.profile._id
    const description = this.description
    const favoriteSpaces = this.favoriteSpaces
    const tagIds = this.interestIds

    this.profileService.updateProfile(profileId, description, favoriteSpaces, tagIds).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(() => {
        this.refreshProfile()
      })
  }

}
